import { supabase } from './supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Documents / Checklist system — data layer (Phase 1: build & store).
// ─────────────────────────────────────────────────────────────────────────────

export type DocumentBlockType =
  | 'heading'
  | 'rich_text'
  | 'divider'
  | 'image'
  | 'checklist'
  | 'acknowledge'
  | 'multiple_choice'
  | 'short_text'
  | 'long_text'
  | 'number'
  | 'date'
  | 'time'
  | 'signature'
  | 'file_upload'
  | 'photo'
  | 'table'
  | 'additional_signatures';

export type DocumentAccessScope = 'admin_only' | 'public' | 'restricted';
export type DocumentStatus = 'draft' | 'published' | 'archived';
export type AssignmentTargetType = 'everyone' | 'role' | 'resource';
export type AssignmentRequirement = 'optional' | 'required';
export type AssignmentRecurrence = 'once' | 'daily' | 'per_shift' | 'per_booking';

export interface DocumentBlock {
  id: string;
  template_id?: string;
  business_id?: string;
  sort_order: number;
  type: DocumentBlockType;
  config: Record<string, any>;
  required: boolean;
}

export interface DocumentAssignment {
  id?: string;
  template_id?: string;
  business_id?: string;
  target_type: AssignmentTargetType;
  target_id: string | null;
  requirement: AssignmentRequirement;
  recurrence: AssignmentRecurrence;
}

export interface DocumentCategory {
  id: string;
  business_id: string;
  name: string;
  color: string;
  sort_order: number;
}

export interface DocumentTemplate {
  id: string;
  business_id: string;
  title: string;
  description: string | null;
  category: string | null;
  category_id: string | null;
  tags: string[];
  status: DocumentStatus;
  access_scope: DocumentAccessScope;
  settings: Record<string, any>;
  version: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentTemplateFull extends DocumentTemplate {
  blocks: DocumentBlock[];
  assignments: DocumentAssignment[];
}

export interface CreateDocumentTemplateParams {
  business_id: string;
  title: string;
  description?: string | null;
  category_id?: string | null;
  tags?: string[];
  access_scope?: DocumentAccessScope;
  settings?: Record<string, any>;
  created_by?: string | null;
}

function ensure() {
  if (!supabase) throw new Error('Supabase client not initialized');
  return supabase;
}

/** Default config for a freshly-added block of a given type. */
export function defaultBlockConfig(type: DocumentBlockType): Record<string, any> {
  switch (type) {
    case 'heading':
      return { text: 'Section heading', size: 'h2' };
    case 'rich_text':
      return { text: '' };
    case 'image':
      return { url: '', caption: '' };
    case 'divider':
      return {};
    case 'checklist':
      return { label: 'Checklist', items: [{ id: cryptoId(), text: 'Item 1' }] };
    case 'acknowledge':
      return { label: 'I have read and understand the above.' };
    case 'multiple_choice':
      return { label: 'Question', multiple: false, options: [{ id: cryptoId(), text: 'Option 1' }, { id: cryptoId(), text: 'Option 2' }] };
    case 'short_text':
      return { label: 'Short answer', placeholder: '' };
    case 'long_text':
      return { label: 'Long answer', placeholder: '' };
    case 'number':
      return { label: 'Number', unit: '' };
    case 'date':
      return { label: 'Date', autoStampAtCompletion: false };
    case 'time':
      return { label: 'Time' };
    case 'signature':
      return { label: 'Signature', who: 'resource' };
    case 'file_upload':
      return { label: 'Upload file' };
    case 'photo':
      return { label: 'Add photo' };
    case 'table':
      return {
        label: 'Table',
        columns: [
          { id: cryptoId(), label: 'Column 1' },
          { id: cryptoId(), label: 'Column 2' },
        ],
        rows: [{ id: cryptoId(), cells: {} }],
        allowAddRows: true,
      };
    case 'additional_signatures':
      return { label: 'Additional signatures' };
    default:
      return {};
  }
}

export function cryptoId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `id_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
}

export const BLOCK_LABELS: Record<DocumentBlockType, string> = {
  heading: 'Heading',
  rich_text: 'Text / Instructions',
  divider: 'Divider',
  image: 'Image',
  checklist: 'Checklist',
  acknowledge: 'Acknowledgement',
  multiple_choice: 'Multiple choice',
  short_text: 'Short answer',
  long_text: 'Long answer',
  number: 'Number',
  date: 'Date',
  time: 'Time',
  signature: 'Signature',
  file_upload: 'File upload',
  photo: 'Photo',
  table: 'Table / Columns',
  additional_signatures: 'Additional signatures',
};

// ── Templates ────────────────────────────────────────────────
export async function listDocumentTemplates(businessId: string): Promise<DocumentTemplate[]> {
  const sb = ensure();
  const { data, error } = await sb
    .from('document_templates')
    .select('*')
    .eq('business_id', businessId)
    .neq('status', 'archived')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data || []) as DocumentTemplate[];
}

export async function getDocumentTemplate(id: string): Promise<DocumentTemplateFull | null> {
  const sb = ensure();
  const { data: template, error } = await sb
    .from('document_templates')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  if (!template) return null;

  const [{ data: blocks }, { data: assignments }] = await Promise.all([
    sb.from('document_blocks').select('*').eq('template_id', id).order('sort_order', { ascending: true }),
    sb.from('document_assignments').select('*').eq('template_id', id),
  ]);

  return {
    ...(template as DocumentTemplate),
    blocks: (blocks || []) as DocumentBlock[],
    assignments: (assignments || []) as DocumentAssignment[],
  };
}

export async function createDocumentTemplate(params: CreateDocumentTemplateParams): Promise<DocumentTemplate> {
  const sb = ensure();
  const { data, error } = await sb
    .from('document_templates')
    .insert({
      business_id: params.business_id,
      title: params.title,
      description: params.description ?? null,
      category_id: params.category_id ?? null,
      tags: params.tags ?? [],
      access_scope: params.access_scope ?? 'admin_only',
      settings: params.settings ?? {},
      created_by: params.created_by ?? null,
      status: 'draft',
    })
    .select()
    .single();
  if (error) throw error;
  return data as DocumentTemplate;
}

export async function updateDocumentTemplate(
  id: string,
  updates: Partial<Pick<DocumentTemplate, 'title' | 'description' | 'category_id' | 'tags' | 'status' | 'access_scope' | 'settings'>>,
): Promise<void> {
  const sb = ensure();
  const { error } = await sb
    .from('document_templates')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteDocumentTemplate(id: string): Promise<void> {
  const sb = ensure();
  const { error } = await sb.from('document_templates').delete().eq('id', id);
  if (error) throw error;
}

/** Replace all blocks for a template (delete + reinsert in order). */
export async function saveDocumentBlocks(
  templateId: string,
  businessId: string,
  blocks: DocumentBlock[],
): Promise<void> {
  const sb = ensure();
  const { error: delErr } = await sb.from('document_blocks').delete().eq('template_id', templateId);
  if (delErr) throw delErr;

  if (blocks.length === 0) return;
  const rows = blocks.map((b, i) => ({
    template_id: templateId,
    business_id: businessId,
    sort_order: i,
    type: b.type,
    config: b.config ?? {},
    required: !!b.required,
  }));
  const { error: insErr } = await sb.from('document_blocks').insert(rows);
  if (insErr) throw insErr;
}

/** Replace all assignments for a template. */
export async function saveDocumentAssignments(
  templateId: string,
  businessId: string,
  assignments: DocumentAssignment[],
): Promise<void> {
  const sb = ensure();
  const { error: delErr } = await sb.from('document_assignments').delete().eq('template_id', templateId);
  if (delErr) throw delErr;

  if (assignments.length === 0) return;
  const rows = assignments.map((a) => ({
    template_id: templateId,
    business_id: businessId,
    target_type: a.target_type,
    target_id: a.target_id,
    requirement: a.requirement,
    recurrence: a.recurrence,
  }));
  const { error: insErr } = await sb.from('document_assignments').insert(rows);
  if (insErr) throw insErr;
}

// ── Categories ───────────────────────────────────────────────
export const CATEGORY_COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4', '#64748b'];

export async function listDocumentCategories(businessId: string): Promise<DocumentCategory[]> {
  const sb = ensure();
  const { data, error } = await sb
    .from('document_categories')
    .select('*')
    .eq('business_id', businessId)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw error;
  return (data || []) as DocumentCategory[];
}

export async function createDocumentCategory(businessId: string, name: string, color?: string): Promise<DocumentCategory> {
  const sb = ensure();
  const { data, error } = await sb
    .from('document_categories')
    .insert({ business_id: businessId, name: name.trim(), color: color || CATEGORY_COLORS[0] })
    .select()
    .single();
  if (error) throw error;
  return data as DocumentCategory;
}

export async function updateDocumentCategory(id: string, updates: Partial<Pick<DocumentCategory, 'name' | 'color' | 'sort_order'>>): Promise<void> {
  const sb = ensure();
  const { error } = await sb.from('document_categories').update(updates).eq('id', id);
  if (error) throw error;
}

export async function deleteDocumentCategory(id: string): Promise<void> {
  const sb = ensure();
  const { error } = await sb.from('document_categories').delete().eq('id', id);
  if (error) throw error;
}

// ── Completion / submissions ─────────────────────────────────
export interface DocumentSubmission {
  id: string;
  template_id: string | null;
  business_id: string;
  title: string | null;
  status: 'in_progress' | 'submitted' | 'voided';
  template_snapshot: DocumentBlock[];
  submitted_by: string | null;
  submitted_by_name: string | null;
  submitted_by_email: string | null;
  content_hash: string | null;
  consent_at: string | null;
  consent_text: string | null;
  ip_address: string | null;
  user_agent: string | null;
  submitted_at: string | null;
  created_at: string;
  // Verification sign-off (two-stage completion)
  verification_status?: 'none' | 'pending' | 'verified';
  verification_procedure_id?: string | null;
  verified_by_user_id?: string | null;
  verified_by_resource_id?: string | null;
  verified_by_name?: string | null;
  verified_at?: string | null;
  verification_hash?: string | null;
}

export interface DocumentResponse { id: string; block_id: string; block_type: string | null; value: any }
export interface DocumentSignatureRow { id: string; block_id: string; signer_name: string | null; signature_data: string | null; signed_at: string; ip_address: string | null; is_verification?: boolean }
export interface DocumentAuditEvent { id: string; event_type: string; actor_name: string | null; ip_address: string | null; user_agent: string | null; metadata: any; created_at: string }

export interface DocumentSubmissionFull extends DocumentSubmission {
  responses: DocumentResponse[];
  signatures: DocumentSignatureRow[];
  audit: DocumentAuditEvent[];
}

/** The current logged-in user's resource id + role id within a business. */
export async function getMyResourceContext(businessId: string): Promise<{ resourceId: string | null; roleId: string | null }> {
  const sb = ensure();
  const { data: { user } } = await sb.auth.getUser();
  if (!user) return { resourceId: null, roleId: null };
  const { data } = await sb
    .from('resources')
    .select('id, role_id')
    .eq('auth_user_id', user.id)
    .eq('business_id', businessId)
    .maybeSingle();
  return { resourceId: (data as any)?.id ?? null, roleId: (data as any)?.role_id ?? null };
}

/**
 * Published templates the user may complete from the quick-add menu.
 * Access is the UNION of:
 *   1. The document's own rule (public, or restricted-assigned to their role/them/everyone), and
 *   2. Any active procedure that targets them (everyone/role/resource) referencing the document —
 *      a procedure grant overrides a stricter document-level rule (e.g. an admin-only doc
 *      becomes visible to a Detailer if a procedure assigns it to that role).
 */
export async function listAccessibleTemplates(
  businessId: string,
  resourceId: string | null,
  roleId: string | null,
  includeAdminOnly = false,
): Promise<DocumentTemplate[]> {
  const sb = ensure();
  const { data: templates, error } = await sb
    .from('document_templates')
    .select('*')
    .eq('business_id', businessId)
    .eq('status', 'published')
    .order('title', { ascending: true });
  if (error) throw error;
  const all = (templates || []) as DocumentTemplate[];
  const byId = new Map(all.map((t) => [t.id, t]));

  const result: DocumentTemplate[] = [];
  const includedIds = new Set<string>();
  const add = (t?: DocumentTemplate) => { if (t && !includedIds.has(t.id)) { includedIds.add(t.id); result.push(t); } };

  // 1) Document-level access
  const restrictedIds: string[] = [];
  for (const t of all) {
    if (t.access_scope === 'public') add(t);
    else if (t.access_scope === 'admin_only' && includeAdminOnly) add(t);
    else if (t.access_scope === 'restricted') restrictedIds.push(t.id);
  }
  if (restrictedIds.length) {
    const { data: assigns } = await sb
      .from('document_assignments')
      .select('template_id, target_type, target_id')
      .in('template_id', restrictedIds);
    const allowed = new Set<string>();
    for (const a of assigns || []) {
      if (a.target_type === 'everyone') allowed.add(a.template_id);
      else if (a.target_type === 'role' && roleId && a.target_id === roleId) allowed.add(a.template_id);
      else if (a.target_type === 'resource' && resourceId && a.target_id === resourceId) allowed.add(a.template_id);
    }
    for (const t of all) if (allowed.has(t.id)) add(t);
  }

  // 2) Procedure-granted access (overrides stricter document-level rules)
  try {
    const { data: procs } = await sb
      .from('document_procedures')
      .select('id, template_id')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .not('template_id', 'is', null);
    const procList = (procs || []) as { id: string; template_id: string }[];
    if (procList.length) {
      const { data: ptargets } = await sb
        .from('document_procedure_targets')
        .select('procedure_id, target_type, target_id')
        .in('procedure_id', procList.map((p) => p.id));
      const grantedProcIds = new Set<string>();
      for (const t of ptargets || []) {
        if (t.target_type === 'everyone' ||
            (t.target_type === 'role' && roleId && t.target_id === roleId) ||
            (t.target_type === 'resource' && resourceId && t.target_id === resourceId)) {
          grantedProcIds.add(t.procedure_id);
        }
      }
      for (const p of procList) {
        if (grantedProcIds.has(p.id)) add(byId.get(p.template_id));
      }
    }
  } catch (e) {
    console.error('Procedure visibility lookup failed:', e);
  }

  return result;
}

/** Submit a completed document (server finalizes: hash + lock + audit). */
export async function submitDocument(params: {
  templateId: string;
  responses: { block_id: string; block_type?: string; value: any }[];
  signatures: { block_id: string; signature_data: string }[];
  consent: boolean;
  /** Link this submission to a booking (so it counts toward the booking's required docs). */
  contextType?: 'standalone' | 'booking' | 'clock_event';
  contextId?: string | null;
}): Promise<{ submissionId: string; contentHash: string }> {
  const sb = ensure();
  const { data, error } = await sb.functions.invoke('submit-document', { body: params });
  if (error) {
    const msg = (error as any)?.context?.error || (error as any)?.message || 'Failed to submit document';
    throw new Error(msg);
  }
  if (!data?.success) throw new Error(data?.error || 'Failed to submit document');
  return { submissionId: data.submissionId, contentHash: data.contentHash };
}

export async function listDocumentSubmissions(
  businessId: string,
  opts: { templateId?: string; resourceId?: string; from?: string; to?: string } = {},
): Promise<DocumentSubmission[]> {
  const sb = ensure();
  let q = sb
    .from('document_submissions')
    .select('id, template_id, business_id, title, status, submitted_by, submitted_by_name, submitted_by_email, content_hash, submitted_at, created_at')
    .eq('business_id', businessId)
    .eq('status', 'submitted')
    .order('submitted_at', { ascending: false });
  if (opts.templateId) q = q.eq('template_id', opts.templateId);
  if (opts.resourceId) q = q.eq('submitted_by', opts.resourceId);
  if (opts.from) q = q.gte('submitted_at', opts.from);
  if (opts.to) q = q.lte('submitted_at', opts.to);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []) as DocumentSubmission[];
}

export async function getDocumentSubmission(id: string): Promise<DocumentSubmissionFull | null> {
  const sb = ensure();
  const { data: sub, error } = await sb.from('document_submissions').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  if (!sub) return null;
  const [{ data: responses }, { data: signatures }, { data: audit }] = await Promise.all([
    sb.from('document_responses').select('*').eq('submission_id', id),
    sb.from('document_signatures').select('*').eq('submission_id', id),
    sb.from('document_audit_events').select('*').eq('submission_id', id).order('created_at', { ascending: true }),
  ]);
  return {
    ...(sub as DocumentSubmission),
    responses: (responses || []) as DocumentResponse[],
    signatures: (signatures || []) as DocumentSignatureRow[],
    audit: (audit || []) as DocumentAuditEvent[],
  };
}

// ── Verification sign-off ────────────────────────────────────
export interface PendingDocumentVerification {
  submission_id: string;
  template_id: string | null;
  title: string | null;
  submitted_by_name: string | null;
  submitted_at: string | null;
  procedure_name: string | null;
}

/** Submissions awaiting sign-off that the current user is the assigned verifier for. */
export async function getPendingDocumentVerifications(businessId: string): Promise<PendingDocumentVerification[]> {
  const sb = ensure();
  const { data, error } = await sb.rpc('get_pending_document_verifications', { p_business_id: businessId });
  if (error) {
    console.error('getPendingDocumentVerifications error:', error);
    return [];
  }
  return (data as PendingDocumentVerification[]) || [];
}

/** Full submission (snapshot + responses + signatures) for the verifier's review. */
export async function getVerificationSubmission(submissionId: string): Promise<{
  submission: DocumentSubmission;
  responses: DocumentResponse[];
  signatures: DocumentSignatureRow[];
} | null> {
  const sb = ensure();
  const { data, error } = await sb.rpc('get_verification_submission', { p_submission_id: submissionId });
  if (error || !data) {
    if (error) console.error('getVerificationSubmission error:', error);
    return null;
  }
  const payload = data as any;
  if (!payload.submission) return null;
  return {
    submission: payload.submission as DocumentSubmission,
    responses: (payload.responses || []) as DocumentResponse[],
    signatures: (payload.signatures || []) as DocumentSignatureRow[],
  };
}

/** Sign off on a pending submission (server appends verifier signature + finalizes). */
export async function verifyDocument(params: {
  submissionId: string;
  signatureData: string;
  note?: string;
}): Promise<void> {
  const sb = ensure();
  const { data, error } = await sb.functions.invoke('verify-document', {
    body: { submissionId: params.submissionId, signatureData: params.signatureData, note: params.note },
  });
  if (error) {
    const msg = (error as any)?.context?.error || (error as any)?.message || 'Failed to sign off document';
    throw new Error(msg);
  }
  if (!data?.success) throw new Error(data?.error || 'Failed to sign off document');
}

// ── Booking documents ────────────────────────────────────────
export interface BookingDocumentRequirement {
  procedure_id: string;
  template_id: string;
  title: string | null;
  facing: ProcedureFacing;
  enforcement: ProcedureEnforcement;
  completed: boolean;
  submission_id: string | null;
}

/** Active booking documents + completion status for a specific booking. */
export async function getBookingDocumentRequirements(businessId: string, bookingId: string): Promise<BookingDocumentRequirement[]> {
  const sb = ensure();
  const { data, error } = await sb.rpc('get_booking_document_requirements', { p_business_id: businessId, p_booking_id: bookingId });
  if (error) { console.error('getBookingDocumentRequirements error:', error); return []; }
  return (data as BookingDocumentRequirement[]) || [];
}

/** Admin-only: log an override when force-completing a booking despite a required doc. */
export async function recordBookingDocumentOverride(params: { businessId: string; bookingId: string; templateId?: string | null; reason: string }): Promise<void> {
  const sb = ensure();
  const { error } = await sb.rpc('record_booking_document_override', {
    p_business_id: params.businessId,
    p_booking_id: params.bookingId,
    p_template_id: params.templateId ?? null,
    p_reason: params.reason,
  });
  if (error) throw error;
}

// ── Procedures (automations for documents) ───────────────────
export type ProcedureTargetType = 'everyone' | 'role' | 'resource' | 'owner';
export type ProcedureTriggerEvent = 'clock_in' | 'clock_out' | 'break_start' | 'break_end' | 'manual';
/** 'completion' = who/when a doc must be done; 'verification' = sign-off after completion; 'booking' = attached to bookings. */
export type ProcedureKind = 'completion' | 'verification' | 'booking';
export type ProcedureFacing = 'staff' | 'customer';
export type ProcedureEnforcement = 'required' | 'optional';

export const PROCEDURE_TRIGGER_EVENTS: { id: ProcedureTriggerEvent; label: string; description: string }[] = [
  { id: 'clock_in', label: 'Clock in', description: 'When the team member clocks in' },
  { id: 'clock_out', label: 'Clock out', description: 'When the team member clocks out' },
  { id: 'break_start', label: 'Break start', description: 'When a break is started' },
  { id: 'break_end', label: 'Break end', description: 'When a break ends' },
  { id: 'manual', label: 'Manual', description: 'Available to complete any time' },
];

export interface DocumentProcedureTarget { id?: string; target_type: ProcedureTargetType; target_id: string | null }
export interface DocumentProcedureTrigger { id?: string; event: ProcedureTriggerEvent; mandatory: boolean }

export interface DocumentProcedure {
  id: string;
  business_id: string;
  name: string;
  template_id: string | null;
  is_active: boolean;
  kind: ProcedureKind;
  /** Booking docs only: who completes it. */
  facing?: ProcedureFacing | null;
  /** Booking docs only: whether it blocks booking completion. */
  enforcement?: ProcedureEnforcement | null;
  created_at: string;
  updated_at: string;
  template_title?: string | null;
  targets: DocumentProcedureTarget[];
  triggers: DocumentProcedureTrigger[];
}

export async function listDocumentProcedures(businessId: string): Promise<DocumentProcedure[]> {
  const sb = ensure();
  const { data: procs, error } = await sb
    .from('document_procedures')
    .select('*, template:document_templates(title)')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  const list = (procs || []) as any[];
  if (list.length === 0) return [];
  const ids = list.map((p) => p.id);
  const [{ data: targets }, { data: triggers }] = await Promise.all([
    sb.from('document_procedure_targets').select('*').in('procedure_id', ids),
    sb.from('document_procedure_triggers').select('*').in('procedure_id', ids),
  ]);
  return list.map((p) => ({
    ...p,
    template_title: p.template?.title ?? null,
    targets: (targets || []).filter((t: any) => t.procedure_id === p.id).map((t: any) => ({ id: t.id, target_type: t.target_type, target_id: t.target_id })),
    triggers: (triggers || []).filter((t: any) => t.procedure_id === p.id).map((t: any) => ({ id: t.id, event: t.event, mandatory: t.mandatory })),
  })) as DocumentProcedure[];
}

export async function saveDocumentProcedure(params: {
  id?: string;
  business_id: string;
  name: string;
  template_id: string;
  is_active?: boolean;
  kind?: ProcedureKind;
  facing?: ProcedureFacing | null;
  enforcement?: ProcedureEnforcement | null;
  targets: DocumentProcedureTarget[];
  triggers: DocumentProcedureTrigger[];
  created_by?: string | null;
}): Promise<string> {
  const sb = ensure();
  const kind: ProcedureKind = params.kind ?? 'completion';
  const facing = kind === 'booking' ? (params.facing ?? 'staff') : null;
  const enforcement = kind === 'booking' ? (params.enforcement ?? 'required') : null;
  let procedureId = params.id;
  if (procedureId) {
    const { error } = await sb
      .from('document_procedures')
      .update({ name: params.name, template_id: params.template_id, is_active: params.is_active ?? true, kind, facing, enforcement, updated_at: new Date().toISOString() })
      .eq('id', procedureId);
    if (error) throw error;
    await sb.from('document_procedure_targets').delete().eq('procedure_id', procedureId);
    await sb.from('document_procedure_triggers').delete().eq('procedure_id', procedureId);
  } else {
    const { data, error } = await sb
      .from('document_procedures')
      .insert({ business_id: params.business_id, name: params.name, template_id: params.template_id, is_active: params.is_active ?? true, kind, facing, enforcement, created_by: params.created_by ?? null })
      .select('id')
      .single();
    if (error) throw error;
    procedureId = (data as any).id;
  }
  if (params.targets.length) {
    const { error } = await sb.from('document_procedure_targets').insert(
      params.targets.map((t) => ({ procedure_id: procedureId, business_id: params.business_id, target_type: t.target_type, target_id: (t.target_type === 'everyone' || t.target_type === 'owner') ? null : t.target_id })),
    );
    if (error) throw error;
  }
  if (params.triggers.length) {
    const { error } = await sb.from('document_procedure_triggers').insert(
      params.triggers.map((t) => ({ procedure_id: procedureId, business_id: params.business_id, event: t.event, mandatory: t.mandatory })),
    );
    if (error) throw error;
  }
  return procedureId!;
}

export async function setDocumentProcedureActive(id: string, isActive: boolean): Promise<void> {
  const sb = ensure();
  const { error } = await sb.from('document_procedures').update({ is_active: isActive, updated_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
}

export async function deleteDocumentProcedure(id: string): Promise<void> {
  const sb = ensure();
  const { error } = await sb.from('document_procedures').delete().eq('id', id);
  if (error) throw error;
}

/** Procedures that apply to a given resource for an event (for clock-time enforcement). */
/**
 * Resource-safe version of the procedure lookup used at clock events.
 * Resources are not in business_users, so they cannot read the procedure tables
 * directly (RLS). This calls a SECURITY DEFINER RPC that resolves the caller's
 * resource + role server-side and returns only the procedures targeting them.
 */
export async function getClockProcedures(
  businessId: string,
  event: ProcedureTriggerEvent,
): Promise<{ template_id: string; mandatory: boolean }[]> {
  const sb = ensure();
  const { data, error } = await sb.rpc('get_clock_procedures', {
    p_business_id: businessId,
    p_event: event,
  });
  if (error) {
    console.error('getClockProcedures error:', error);
    return [];
  }
  return ((data as any[]) || [])
    .filter((r) => r.template_id)
    .map((r) => ({ template_id: r.template_id as string, mandatory: !!r.mandatory }));
}

/**
 * Resource-safe fetch of a procedure's template + blocks (RLS would otherwise
 * hide it from a resource). Returns null if the caller isn't authorised.
 */
export async function getProcedureTemplate(id: string): Promise<DocumentTemplateFull | null> {
  const sb = ensure();
  const { data, error } = await sb.rpc('get_procedure_template', { p_template_id: id });
  if (error || !data) {
    if (error) console.error('getProcedureTemplate error:', error);
    return null;
  }
  const tpl = (data as any).template;
  if (!tpl) return null;
  return {
    ...(tpl as DocumentTemplate),
    blocks: (((data as any).blocks as DocumentBlock[]) || []),
    assignments: [],
  };
}

export async function listProceduresForEvent(
  businessId: string,
  resourceId: string | null,
  roleId: string | null,
  event: ProcedureTriggerEvent,
): Promise<{ procedure: DocumentProcedure; mandatory: boolean }[]> {
  const all = await listDocumentProcedures(businessId);
  const out: { procedure: DocumentProcedure; mandatory: boolean }[] = [];
  for (const p of all) {
    if (!p.is_active || !p.template_id) continue;
    const trig = p.triggers.find((t) => t.event === event);
    if (!trig) continue;
    const applies = p.targets.some((t) =>
      t.target_type === 'everyone' ||
      (t.target_type === 'role' && roleId && t.target_id === roleId) ||
      (t.target_type === 'resource' && resourceId && t.target_id === resourceId));
    if (applies) out.push({ procedure: p, mandatory: trig.mandatory });
  }
  return out;
}

// ── AI generation ────────────────────────────────────────────
export interface AIDocumentDraft {
  title: string;
  description: string;
  suggestedCategory: string;
  blocks: { type: DocumentBlockType; required: boolean; config: Record<string, any> }[];
}

export async function generateDocumentWithAI(params: {
  businessId: string;
  businessName?: string;
  mode: 'prompt' | 'pdf';
  prompt?: string;
  fileBase64?: string;
  fileMime?: string;
  fileName?: string;
}): Promise<AIDocumentDraft> {
  const sb = ensure();
  const { data, error } = await sb.functions.invoke('ai-create-document', { body: params });
  if (error) {
    // Try to surface the function's JSON error body if present
    const msg = (error as any)?.context?.error || (error as any)?.message || 'Failed to generate document';
    throw new Error(msg);
  }
  if (!data?.success) {
    throw new Error(data?.error || 'Failed to generate document');
  }
  return data.document as AIDocumentDraft;
}

/** Per-block AI edit: send the block's current config + an instruction, get an updated config back. */
export async function aiEditBlock(params: {
  businessId: string;
  businessName?: string;
  type: DocumentBlockType;
  config: Record<string, any>;
  instruction: string;
}): Promise<Record<string, any>> {
  const sb = ensure();
  const { data, error } = await sb.functions.invoke('ai-edit-document-block', { body: params });
  if (error) {
    const msg = (error as any)?.context?.error || (error as any)?.message || 'Failed to edit section';
    throw new Error(msg);
  }
  if (!data?.success) throw new Error(data?.error || 'Failed to edit section');

  // Merge with defaults + reassign ids on list items/options.
  const config: Record<string, any> = { ...defaultBlockConfig(params.type), ...(data.config || {}) };
  if (params.type === 'checklist' && Array.isArray(config.items)) {
    config.items = config.items.map((it: any) => ({ id: cryptoId(), text: typeof it === 'string' ? it : (it?.text || '') }));
  }
  if (params.type === 'multiple_choice' && Array.isArray(config.options)) {
    config.options = config.options.map((o: any) => ({ id: cryptoId(), text: typeof o === 'string' ? o : (o?.text || '') }));
  }
  return config;
}

/** Convert AI block specs into builder-ready blocks (assign ids, merge defaults). */
export function aiBlocksToDocumentBlocks(aiBlocks: AIDocumentDraft['blocks']): DocumentBlock[] {
  return (aiBlocks || []).map((b, i) => {
    const config: Record<string, any> = { ...defaultBlockConfig(b.type), ...(b.config || {}) };
    if (b.type === 'checklist' && Array.isArray(config.items)) {
      config.items = config.items.map((it: any) => ({ id: cryptoId(), text: typeof it === 'string' ? it : (it?.text || '') }));
    }
    if (b.type === 'multiple_choice' && Array.isArray(config.options)) {
      config.options = config.options.map((o: any) => ({ id: cryptoId(), text: typeof o === 'string' ? o : (o?.text || '') }));
    }
    if (b.type === 'table') {
      const columns = Array.isArray(config.columns) && config.columns.length
        ? config.columns.map((c: any) => ({ id: cryptoId(), label: typeof c === 'string' ? c : (c?.label || 'Column') }))
        : [{ id: cryptoId(), label: 'Column 1' }, { id: cryptoId(), label: 'Column 2' }];
      config.columns = columns;
      config.rows = [{ id: cryptoId(), cells: {} }];
      config.allowAddRows = config.allowAddRows !== false;
    }
    // Display-only blocks can never be "required" (they have no answer to fill in).
    const displayOnly = ['heading', 'rich_text', 'divider', 'image'].includes(b.type);
    return { id: cryptoId(), sort_order: i, type: b.type, config, required: displayOnly ? false : !!b.required };
  });
}
